import { create } from 'zustand';

import { getUserEvents, getEvent } from '../services/api/events';

import { Event, PageError } from '../types';
import { useUserStore } from './user';

interface ProjectStore {
  userEvents: Event[];
  eventId: string;
  currentProject: Event | null;
  projectError: PageError;

  setUserEvents: (userEvents: Event[]) => void;
  clearEventId: () => void;
  setEventId: (eventId: string, isAllowAssignees?: boolean) => Promise<void>;
  setSampleProject: (eventId: string) => void;
  updateCurrentEvent: () => Promise<void>;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  userEvents: [],
  eventId: '',
  currentProject: null,
  projectError: null,

  setUserEvents: (userEvents) => set({ userEvents }),

  clearEventId: () => set({ eventId: '', currentProject: null }),

  setEventId: async (eventId, isAllowAssignees) => {
    // get the event from list of user's events
    let ownedProject = get().userEvents.find((e) => e.eventId === eventId);

    // if user owns the project
    if (ownedProject) {
      set({ eventId, currentProject: ownedProject, projectError: null });
      return;
    }

    const currentProject = await getEvent(eventId, isAllowAssignees);

    // if event doesn't exist
    if (!currentProject)
      set({ eventId, currentProject: null, projectError: 404 });
    // if user has no access to the page
    else if (!isAllowAssignees)
      set({ eventId, currentProject: null, projectError: 403 });
    // if it's assignee accessing the page
    else
      set({
        eventId,
        currentProject: { ...currentProject, isAssigneeAccess: true },
        projectError: null,
      });
  },

  setSampleProject: (eventId) => {
    const sampleProjectData = JSON.parse(
      localStorage.getItem('sampleProjectData') ?? '{}',
    );

    set({ eventId, currentProject: sampleProjectData.event });
  },

  updateCurrentEvent: async () => {
    const userId = useUserStore.getState().user?.session.userId ?? null;
    const eventId = get().currentProject?.eventId ?? '';

    // update user events list and current event
    const userEvents = userId ? await getUserEvents(userId) : [];
    const currentProject = eventId ? await getEvent(eventId) : null;

    set({ eventId, userEvents, currentProject });
  },
}));
